import { FC } from "react";

import { Flex } from "@chakra-ui/react";
import { TFunction, useTranslation } from "next-i18next";

import { FullWidthVStack } from "@components/common";
import { ContextMessage } from "@components/Message";
import PageHeader from "@components/PageHeader";
import { Desktop, Mobile } from "@lib/media";
import { LayoutContent } from "@modules/layout";
import Notifications, { INotifications } from "@modules/notifications";

import { ORDER_MESSAGE_ZONE, OrderStep } from "../constants";
import OrderContextProvider from "../context/OrderContextProvider";
import type { TPaymentStatus, TStaticInitialOrderData } from "../types";
import OrderSteps from "./common/OrderSteps";
import OrderPageContent from "./OrderPageContent";
import Success from "./success/Success";

type TOrderPageContentProps = {
  currentStep: OrderStep;
  notifications?: INotifications | null;
  orderData?: TStaticInitialOrderData;
  paymentStatus?: TPaymentStatus;
  weightRegulations: string[] | null;
  shipmentId?: string;
};

const getPageHeaderTitle = (t: TFunction, step: OrderStep): string => {
  const titlesMap = {
    [OrderStep.PackageInfo]: t("order-package-header_title"),
    [OrderStep.ShipmentDetails]: t("order-shipment-header_title"),
    [OrderStep.SenderDetails]: t("order-sender-header_title"),
    [OrderStep.ReceiverDetails]: t("order-receiver-header_title"),
    [OrderStep.Summary]: t("order-summary-header_title"),
    [OrderStep.Success]: t("order-success-header_title"),
  };

  return titlesMap[step] || "";
};

const OrderPage: FC<TOrderPageContentProps> = ({
  currentStep,
  notifications,
  orderData,
  weightRegulations,
  paymentStatus,
  shipmentId,
}) => {
  const { t } = useTranslation();

  const pageContent =
    currentStep === OrderStep.Success ? (
      <Success paymentStatus={paymentStatus!} />
    ) : (
      <OrderPageContent currentStep={currentStep} />
    );

  return (
    <OrderContextProvider currentStep={currentStep} staticOrderData={orderData} shipmentId={shipmentId} weightRegulations={weightRegulations}>
      <PageHeader>{getPageHeaderTitle(t, currentStep)}</PageHeader>
      <LayoutContent sx={{ flex: "1", display: "flex", flexDirection: "column" }}>
        <FullWidthVStack flex="1">
          <Mobile>
            <OrderSteps currentStep={currentStep} isMobile />
          </Mobile>
          {notifications && <Notifications notificationsData={notifications} />}
          <ContextMessage messageZone={ORDER_MESSAGE_ZONE} />
          <Flex mt="1.5rem" flex="1">
            <Desktop>
              <OrderSteps currentStep={currentStep} />
            </Desktop>
            {pageContent}
          </Flex>
        </FullWidthVStack>
      </LayoutContent>
    </OrderContextProvider>
  );
};
export default OrderPage;
